import { Injectable } from '@angular/core';
import { Http, Response, Headers, RequestOptions, URLSearchParams, ResponseContentType }  from '@angular/http';

import { TranslateService } from 'ng2-translate/ng2-translate';
import {Observable} from "rxjs/Observable";

import { ApiService } from "../../shared/network/api.service";
import { HttpService } from '../../shared/network/http.service';
import {AuthService} from "../../shared/authentication/auth.service";

export enum Statuses {
    NOT_STARTED = 0,
    IN_PROGRESS = 1,
    PAUSED = 2,
    FINISHED = 4
}

export enum Codes {
    NO_CHANGES = 0,
    STARTED = 1,
    PAUSED = 2,
    FINISHED = 4,
    LOCATION_CHANGED = 5,
    EPI_CHANGED = 6,
    START_WITH_MASTER_CODE = 7,
    COLLECTIVE_PE_DETECTED = 8
}

export var StatusesTokens = [
    'workorders.status.NOT_STARTED', 'workorders.status.IN_PROGRESS', 'workorders.status.PAUSED',
    'workorders.status.PARTIALLY_FINISHED', 'workorders.status.FINISHED'
]

@Injectable()
export class WorkordersService {

    private baseOrgUrl = function() {
        return this.apiservice.getBaseUrl() + '/organizations/' + this.authService.getSelectedOrg();
    }

    private workordersUrl = this.baseOrgUrl() + '/workorders/';
    private workordersStatisticsUrl = this.baseOrgUrl() + '/workorders/statistics';
    private workordersStatisticsByUserUrl = function(userId) {
        return this.baseOrgUrl() + '/users/' + userId + '/workorders/statistics';
    }
    private activitiesUrl = this.apiservice.getBaseUrl() + '/activities';
    private deficienciesGraphByUser = function(userId) {
        return this.apiservice.getBaseUrl() + '/organizations/' + this.authService.getSelectedOrg() + '/users/' +
                userId + '/deficienciesGraph';
    }

    private getCloneWorkorderUrl = function(woId) {
        return this.baseOrgUrl() + '/workorders/' + woId + '/clone';
    }
    
    private deficienciesGraphByWorkOrder = function(woId) {
        return this.apiservice.getBaseUrl() + '/organizations/' + this.authService.getSelectedOrg() + '/workorders/' +
            woId + '/deficienciesGraph';
    }
    private deficienciesGraphByWorkOrderAndUser = function(woId, userId) {
        return this.apiservice.getBaseUrl() + '/organizations/' + this.authService.getSelectedOrg() + '/workorders/' +
            woId + '/users/' + userId +  '/deficienciesGraph';
    }
    private workorderUserLocationUrl = function(woId, userId) {
        return this.baseOrgUrl() + '/workorders/' + woId + '/users/' + userId + '/lastLocations';
    }
    private workorderUserEventsUrl = function(woId, userId) {
        return this.baseOrgUrl() + '/workorders/' + woId + '/users/' + userId + '/events';
    }

    constructor(
        private http:HttpService,
        private apiservice:ApiService,
        private authService:AuthService,
        private translate: TranslateService
    ) { }

    getWorkordersStatistics(filters, dateFilter, page, limit, sortBy, sortOrder, lang): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let params: URLSearchParams = new URLSearchParams();

        for (let filter of Object.keys(filters)) {
            params.set(filter, filters[filter]);
        }
        if (filters.activity && filters.activity.length > 0) params.set('activity', filters.activity[0].id);
        if (filters.status && filters.status.length > 0) params.set('status', filters.status[0].id);

        if (dateFilter.from) {
            var fromDate = dateFilter.from.date;
            params.set('from', fromDate.year + '-' + ("0" + fromDate.month).slice(-2) + '-' + ("0" + fromDate.day).slice(-2));
        }

        if (dateFilter.to) {
            var toDate = dateFilter.to.date;
            params.set('to', toDate.year + '-' + ("0" + toDate.month).slice(-2) + '-' + ("0" + toDate.day).slice(-2));
        }
        if (page) params.set('page', page);
        if (limit) params.set('limit', limit);
        if (sortBy) params.set('sortBy', sortBy);
        if (sortOrder) params.set('sortOrder', sortOrder);
        params.set('lang', lang);

        let options = new RequestOptions({ headers: headers, search: params });
        return this.http.get(this.workordersStatisticsUrl, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    getWorkordersStatisticsByUser(userId, filters, dateFilter, page, limit, sortBy, sortOrder, lang): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let params: URLSearchParams = new URLSearchParams();

        if (page) params.set('page', page);
        if (limit) params.set('limit', limit);

        let options = new RequestOptions({ headers: headers, search: params });
        return this.http.get(this.workordersStatisticsByUserUrl(userId), options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    getWorkorder(id): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let params: URLSearchParams = new URLSearchParams();

        let options = new RequestOptions({ headers: headers, search: params });
        return this.http.get(this.workordersUrl + id, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    createWorkorder(data, phases): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let options = new RequestOptions({ headers: headers });

        var parsedData = this.generateParsedWorkOrder(data, phases);

        return this.http.post(this.workordersUrl, parsedData, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    updateWorkorder(id, data, phases): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let options = new RequestOptions({ headers: headers });

        var parsedData = this.generateParsedWorkOrder(data, phases);

        return this.http.put(this.workordersUrl + id, parsedData, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    deleteWorkorder(id): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let options = new RequestOptions({ headers: headers });
        return this.http.delete(this.workordersUrl + id, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    getActivites(lang): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});

        let params: URLSearchParams = new URLSearchParams();
        params.set('lang', lang);

        let options = new RequestOptions({ headers: headers, search: params });
        return this.http.get(this.activitiesUrl, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    generateParsedWorkOrder(data, phases) {
        var parsedData = Object.assign({}, data);

        parsedData.estimated_start_date = data.start_date.formatted;
        parsedData.estimated_end_date = data.end_date.formatted;
        delete parsedData.start_date;
        delete parsedData.end_date;

        parsedData.geopoint = {"lat": data.latitude, "long": data.longitude};
        delete parsedData.latitude;
        delete parsedData.longitude;

        parsedData.phases = [];

        for (var phase of phases) {
            var parsedPhase = {
                id: phase.id,
                description : phase.description,
                _activity: phase.activity[0].id,
                _main_contractor: phase.main_contractor[0].id,
                first_contractors: [],
                workers: [],
                collective_eps: []
            }
            for (var contractorIndex in phase.contractors) {
                var contractor = phase.contractors[contractorIndex];
                var parsedContractor = {
                    _id: contractor.value,
                    sub_contractors: []
                };
                var subcontractors = phase['subcontractor'+contractorIndex];
                if (subcontractors) {
                    for (var subcontractor of subcontractors) {
                        parsedContractor.sub_contractors.push(subcontractor.value);
                    }
                }
                parsedPhase.first_contractors.push(parsedContractor);
            }
            for (var workerIndex in phase.workers) {
                var worker = phase.workers[workerIndex];
                var parsedWorker = {
                    worker_id: worker.value,
                    epis: [],
                    roles: []
                }
                if (phase.work_manager && worker.value === phase.work_manager[0].id) {
                    parsedWorker.roles.push(0);
                }
                if (phase.preventive_resource && worker.value === phase.preventive_resource[0].id) {
                    parsedWorker.roles.push(1);
                }
                parsedWorker.roles.push(2);

                var epis = phase['worker'+workerIndex];
                if (epis) {
                    for (var epi of epis) {
                        parsedWorker.epis.push(epi.value);
                    }
                }

                parsedPhase.workers.push(parsedWorker)
            }
            for (var ep of phase.collective_eps) {
                parsedPhase.collective_eps.push(ep.value);
            }
            parsedData.phases.push(parsedPhase)
        }
        return parsedData;
    }

    public getDeficienciesGraphSummary(userId, woId, filters, lang): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});

        // TO-DO: Handle filters
        var url;
        if (userId && woId) {
            url = this.deficienciesGraphByWorkOrderAndUser(woId, userId);
        } else if (userId) {
            url = this.deficienciesGraphByUser(userId);
        } else if (woId) {
            url = this.deficienciesGraphByWorkOrder(woId);
        } else {
            // Mocked data for the moment
            return Observable.of({
                parts: ['Head', 'Foot', 'Trunk/Chest'],
                deficiencies: [1, 1, 2]
            });
        }

        let params: URLSearchParams = new URLSearchParams();
        params.set('lang', lang);

        let options = new RequestOptions({ headers: headers, search: params });
        return this.http.get(url, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    getWorkorderUserLastLocations(woId, userId): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});

        let params: URLSearchParams = new URLSearchParams();

        let options = new RequestOptions({ headers: headers, search: params });
        return this.http.get(this.workorderUserLocationUrl(woId, userId), options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    getWorkorderUserEvents(woId, userId, dateFilter): Observable<any> {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let params: URLSearchParams = new URLSearchParams();

        if (dateFilter.from) {
            var fromDate = dateFilter.from.date;
            params.set('from', fromDate.year + '-' + ("0" + fromDate.month).slice(-2) + '-' + ("0" + fromDate.day).slice(-2));
        }

        if (dateFilter.to) {
            var toDate = dateFilter.to.date;
            params.set('to', toDate.year + '-' + ("0" + toDate.month).slice(-2) + '-' + ("0" + toDate.day).slice(-2));
        }

        let options = new RequestOptions({ headers: headers, search: params });
        return this.http.get(this.workorderUserEventsUrl(woId, userId), options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    public getStatusClass(status) {
        var styleClass = 'status';

        switch(parseInt(status)) {
            case Statuses.NOT_STARTED:
                styleClass += ' not-started';
                break;
            case Statuses.IN_PROGRESS:
                styleClass += ' in-progress';
                break;
            case Statuses.PAUSED:
                styleClass += ' paused';
                break;
            case Statuses.FINISHED:
                styleClass += ' finished'
                break;
        }

        return styleClass;
    }

    public getWorkOrderMainContractor(workorder) {
        var contractor = null;

        for (var phase of workorder.phases) {
            if (!contractor) {
                contractor = phase.main_contractor[0];
            } else {
                if (contractor._id !== phase.main_contractor[0]._id) {
                    return this.translate.instant("workorders.MULTIPLE");
                }
            }
        }

        return contractor.name;
    }

    public getWorkOrderActivity(workorder) {
        var activity = null;

        for (var phase of workorder.phases) {
            if (!activity) {
                activity = phase.activity[0];
            } else {
                if (activity._id !== phase.activity[0]._id) {
                    return this.translate.instant("workorders.MULTIPLE");
                }
            }
        }

        return activity.languages.find(item => item.lang === this.translate.currentLang.substring(0, 2)).description;
    }
    
    public postCloneWorkOrder(workorderId, code, startDate, endDate) {
        let headers = new Headers({ 'Authorization': this.authService.getAuthToken()});
        let options = new RequestOptions({ headers: headers });

        return this.http.post(this.getCloneWorkorderUrl(workorderId), {code: code, estimated_start_date: startDate, estimated_end_date: endDate}, options)
            .map(this.extractJSON)
            .catch(this.handleError);
    }

    private extractJSON(res: Response) {
        let body = res.json();
        return body || [];
    }

    private handleError (error: Response | any) {

        let errMsg: string;

        if (error.status == 404) {
            errMsg = error.statusText;
        } else {
            const body = error.json() || '';
            errMsg = body.message || JSON.stringify(body);
        }
        return Observable.throw(errMsg);
    }

}



// WEBPACK FOOTER //
// ./src/app/routes/workorders/workorders.service.ts